// PointerEvent ベースの統合入力ハンドラ。
// マウス / タッチ / タッチペンを同一に扱い、現在のツールに応じて動作を切り替える。
//
// - tool === "draw-pen": pointerdown でストローク開始、pointermove で増分、pointerup で確定
// - tool === "place-*": pointerdown でオブジェクトを配置（仮実装：場所のみ記録）
// - tool === "idle" の空白部 pointerdown: パン開始
// - tool === "idle" のオブジェクト pointerdown: transform 開始（実装は selection-overlay 側）

import { useEffect, useRef, type RefObject } from "react";
import { customAlphabet } from "nanoid";
import { useCanvasStore, type CanvasTool } from "../store/canvas-store";
import { PEN_THICKNESS_PIXELS } from "../../../lib/pen-preference";
import type { StrokePoint } from "../../../lib/pen-stroke";
import { CANVAS_SNAP_PX, screenToStage, type ViewState } from "../../../lib/canvas-coordinates";
import { SECTION_MIN_H, SECTION_MIN_W, clampPointToCanvas } from "@refinement-canvas/domain";
import { resolvePenPreviewRect } from "../../../lib/pen-preview-rect";
import { useCanvasRect } from "../use-canvas-rect";
import { computeShapeDragFrame, type ShapeDragFrame } from "../../../lib/shape-arrow";

const generateStrokeId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);
// 描画中ストロークの他参加者向け中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const PEN_TRANSIENT_THROTTLE_MS = 50;

// マウスのボタン番号（PointerEvent.button）。
const PRIMARY_BUTTON = 0; // 左ボタン
const MIDDLE_BUTTON = 1; // ホイール押し込み
const SECONDARY_BUTTON = 2; // 右ボタン
// 右ボタンドラッグによるパンを「ただの右クリック」と区別するしきい値（クライアント px）。
// これを超えて動いたときだけ、直後の contextmenu を抑止する（use-marquee-selection と揃える）。
const PAN_MOVE_THRESHOLD_PX = 4;

// 図形配置で「シングルクリック」と「ドラッグ」を判別するしきい値（クライアント px）。
// これを超えて初めてドラッグ配置（任意サイズ）として扱い、未満は従来どおり既定サイズで配置する。
const SHAPE_DRAG_THRESHOLD_PX = 5;
// ドラッグ配置で確保する最小サイズ（ステージ px）。退化（0 サイズ）を防ぐ。
// リサイズハンドルの最小サイズ（use-resize-handle.ts の MIN_SIZE_PX = 16）と揃え、
// ドラッグ配置直後にリサイズすると急に拡大される、といった不整合を避ける。
const SHAPE_DRAG_MIN_SIZE_PX = 16;
// 参照専用（埋め込み）でパンを開始するドラッグしきい値（クライアント px）。
// この距離を超えて初めてドラッグとみなしてパンを開始する。しきい値未満のその場クリックは
// パンにせず、リンクカード等の要素側クリック（新規タブで開く等）を活かすため。
const READONLY_PAN_ACTIVATE_PX = 4;

/** ドラッグ配置（任意サイズ）に対応する配置ツール。図形とセクションで同じジェスチャを共有する。 */
type PlaceDragTool = "place-shape" | "place-section";

/**
 * ドラッグ配置の枠計算オプションをツール種別から得る。
 * セクションはタイトルバーが潰れないよう種別固有の最小サイズ（200×120）を確保する
 * （リサイズハンドル側の最小値と揃え、配置直後のリサイズで急に拡大される不整合を避ける）。
 */
function placeDragFrameOptions(tool: PlaceDragTool): {
  gridSnap: boolean;
  snapPx: number;
  minSize: number;
  minW?: number | undefined;
  minH?: number | undefined;
} {
  const base = {
    gridSnap: useCanvasStore.getState().gridSnapEnabled,
    snapPx: CANVAS_SNAP_PX,
    minSize: SHAPE_DRAG_MIN_SIZE_PX,
  };
  if (tool === "place-section") {
    return { ...base, minW: SECTION_MIN_W, minH: SECTION_MIN_H };
  }
  return base;
}

interface PenLayerHandle {
  readonly canvas: HTMLCanvasElement;
}

interface Handlers {
  /**
   * ストローク確定時に呼ばれる。サーバへ pen.stroke.commit op を送る用途。
   * strokeId は描画中の transient（pen.stroke.append）と同一の ID で、確定オブジェクトの
   * ID にこれを採用することで、確定時に該当ストロークのゴーストだけを正確に破棄できる。
   */
  readonly onStrokeCommit: (params: {
    strokeId: string;
    points: readonly StrokePoint[];
    color: string;
    thickness: number;
  }) => void;
  /** 付箋・図形・テキストの配置確定時に呼ばれる（シングルクリック配置）。 */
  readonly onPlaceObject: (params: { stagePoint: { x: number; y: number } }) => void;
  /**
   * 図形をドラッグで任意サイズ配置したときに呼ばれる。frame はグリッド吸着・最小サイズ・
   * キャンバス範囲内 clamp を反映済みのステージ座標枠で、矢印の向き（arrowFromCorner）も含む。
   */
  readonly onPlaceShapeDrag: (params: { frame: ShapeDragFrame }) => void;
  /**
   * セクションをドラッグで任意サイズ配置したときに呼ばれる。frame はグリッド吸着・
   * セクション固有の最小サイズ（200×120）・キャンバス範囲内 clamp を反映済みのステージ座標枠。
   */
  readonly onPlaceSectionDrag: (params: { frame: ShapeDragFrame }) => void;
  /**
   * 描画中ストロークの増分点を他参加者へ中継する用途で呼ばれる（ゴースト表示用、任意）。
   * strokeId はストロークごとに一意で、同一ストロークの append 受信を束ねるために使う。
   */
  readonly onStrokeProgress?:
    | ((params: { strokeId: string; points: readonly StrokePoint[] }) => void)
    | undefined;
}

interface Options {
  readonly stageRef: RefObject<HTMLElement | null>;
  readonly penLayerRef: RefObject<PenLayerHandle | null>;
  /** 参照専用（埋め込み）。true で配置・描画を無効化し、空白ドラッグのパンだけを残す。 */
  readonly readOnly?: boolean | undefined;
}

export function usePointerMode(options: Options, handlers: Handlers): void {
  const readOnly = options.readOnly ?? false;
  // 入力中ペンプレビューの描画座標を、プレビュー用 Canvas の原点分だけずらすために使う。
  // プレビュー用 Canvas は CanvasPenLayer が「固定ステージ ∪ キャンバス矩形」へ配置・リサイズしているため、
  // 座標変換もその和集合矩形の原点で行う（矩形の外側から描き始めたストロークもプレビューへ収める）。
  const penPreviewRect = resolvePenPreviewRect(useCanvasRect());
  const stateRef = useRef<{
    drawing: boolean;
    panning: boolean;
    panStart: { x: number; y: number; panX: number; panY: number } | null;
    // パン終了時に元へ戻すツール（中ボタンパンなどで配置ツール中にパンした場合の復元用）。
    toolBeforePan: CanvasTool | null;
    // このパンが実際に動いたか（右ボタンパン直後の contextmenu 抑止の判定に使う）。
    panMoved: boolean;
    // このパンが右（副）ボタンで開始されたか。動いた場合に直後の contextmenu を抑止する。
    panIsSecondary: boolean;
    points: StrokePoint[];
    // 描画中ストロークの他参加者中継用。strokeId と「未送信点の開始位置」「直近送信時刻」を保持する。
    strokeId: string | null;
    sentPointCount: number;
    lastTransientAt: number;
    // 図形 / セクションのドラッグ配置の進行状態。開始点（ステージ / クライアント座標）と
    // しきい値超過判定、ジェスチャを開始したツール種別を保持する。
    shapePlacing: boolean;
    shapeStart: { stageX: number; stageY: number; clientX: number; clientY: number } | null;
    shapeDragActive: boolean;
    // 参照専用（埋め込み）でのパン保留状態。pointerdown 時点の位置と view を記録し、
    // 一定距離ドラッグされた時点でパンへ昇格させる（その場クリックはパンにしない）。
    // isSecondary は右ボタン起点かどうかで、パン成立後に直後の contextmenu を抑止する判定に使う。
    readOnlyPanPending:
      | {
          x: number;
          y: number;
          panX: number;
          panY: number;
          pointerId: number;
          isSecondary: boolean;
        }
      | null;
    placeDragTool: PlaceDragTool | null;
  }>({
    drawing: false,
    panning: false,
    panStart: null,
    toolBeforePan: null,
    panMoved: false,
    panIsSecondary: false,
    points: [],
    strokeId: null,
    sentPointCount: 0,
    lastTransientAt: 0,
    shapePlacing: false,
    shapeStart: null,
    shapeDragActive: false,
    readOnlyPanPending: null,
    placeDragTool: null,
  });
  // 右ボタンドラッグによるパン直後の contextmenu を 1 回だけ抑止するためのフラグ
  // （use-marquee-selection の同名の仕組みと同趣旨。反転 ON 時の右ドラッグパン用）。
  const suppressContextMenuRef = useRef(false);

  useEffect(() => {
    const container = options.stageRef.current;
    if (!container) return undefined;

    // パン開始の共通処理。開始時のツールを覚えておき、終了時に復元する（中ボタンパンで配置ツール中に
    // パンしても、離したらそのツールへ戻せるようにする）。isSecondary は右ボタン起点かどうかで、
    // 実際に動いた場合に直後の contextmenu を抑止する判定に使う。
    const beginPan = (
      event: PointerEvent,
      view: ViewState,
      toolBeforePan: CanvasTool,
      isSecondary: boolean
    ): void => {
      stateRef.current.panning = true;
      stateRef.current.panStart = {
        x: event.clientX,
        y: event.clientY,
        panX: view.panX,
        panY: view.panY,
      };
      stateRef.current.toolBeforePan = toolBeforePan;
      stateRef.current.panMoved = false;
      stateRef.current.panIsSecondary = isSecondary;
      useCanvasStore.getState().setTool("panning");
      container.setPointerCapture(event.pointerId);
    };

    const onPointerDown = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const tool = useCanvasStore.getState().tool;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      // 新しいジェスチャの開始時に、前ジェスチャで立てた contextmenu 抑止フラグをリセットする
      // （進行中のパン中は落とさない）。
      if (!stateRef.current.panning) suppressContextMenuRef.current = false;

      // 参照専用（埋め込み）: 選択ツールが無いので、左・中・右いずれのボタンのドラッグでもキャンバス移動
      // にする。pointerdown 即時にはパンせず、一定距離ドラッグされて初めてパンへ昇格させる（その場クリックは
      // リンクカード等の要素側クリックを活かすためパンにしない）。中ボタンはブラウザ既定の autoscroll を抑止する。
      if (readOnly) {
        if (event.button === MIDDLE_BUTTON) event.preventDefault();
        if (tool === "idle" && event.target instanceof Node && container.contains(event.target)) {
          stateRef.current.readOnlyPanPending = {
            x: event.clientX,
            y: event.clientY,
            panX: view.panX,
            panY: view.panY,
            pointerId: event.pointerId,
            isSecondary: event.button === SECONDARY_BUTTON,
          };
        }
        return;
      }

      // 選択操作の左右反転。パン用ボタンは反転 OFF=左、反転 ON=右。参照専用では反転しない。
      const invert = useCanvasStore.getState().invertSelectPan;
      const panButton = !readOnly && invert ? SECONDARY_BUTTON : PRIMARY_BUTTON;

      // 中ボタン（ホイール押し込み）ドラッグは、どのツール・どのモードでもキャンバス移動にする
      // （FigJam / Confluence 相当）。カーソル下に図形があっても選択せずに移動できる（object 側は
      // button!==0 の pointerdown を stopPropagation せず無視するため、イベントが container まで伝播する）。
      // 既に描画 / 図形配置ドラッグ / パン中のときは割り込ませない。
      if (
        event.button === MIDDLE_BUTTON &&
        !stateRef.current.drawing &&
        !stateRef.current.shapePlacing &&
        !stateRef.current.panning
      ) {
        // ブラウザ既定の中ボタン autoscroll（丸いスクロールカーソル）を抑止する。
        event.preventDefault();
        beginPan(event, view, tool, false);
        return;
      }

      // 主ボタン（左）以外の処理。反転 ON のパン用ボタン（右）による空白部パンだけをここで扱い、
      // それ以外（図形上の右クリック＝コンテキストメニュー、反転 OFF の右ドラッグ＝マーキー）は
      // use-marquee-selection / canvas-objects-layer 側へ委ねる。
      if (event.button !== PRIMARY_BUTTON) {
        if (
          !readOnly &&
          event.button === panButton &&
          tool === "idle" &&
          event.target === container
        ) {
          // 反転 ON の右ボタンはパン専用。右クリック（ドラッグ無し）は貼り付けメニューを開くだけなので、
          // ここで選択解除はしない（右クリックで意図せず現在の選択が消えるのを防ぐ）。
          beginPan(event, view, tool, true);
        }
        return;
      }

      // ---- 以下は主ボタン（左）のみ（参照専用は上で全ボタン処理済み）----

      if (tool === "draw-pen") {
        const clippedStagePoint = clampPointToCanvas(stagePoint);
        stateRef.current.drawing = true;
        stateRef.current.points = [
          { x: clippedStagePoint.x, y: clippedStagePoint.y, pressure: pressureOf(event) },
        ];
        stateRef.current.strokeId = generateStrokeId();
        stateRef.current.sentPointCount = 0;
        stateRef.current.lastTransientAt = 0;
        container.setPointerCapture(event.pointerId);
        return;
      }

      if (tool === "place-shape" || tool === "place-section") {
        // 当初仕様（キャンバス外にも配置可）へ戻したため、ドラッグ開始点がキャンバス外でも拒否しない。
        // 図形・セクションは「シングルクリック=既定サイズ」「ドラッグ=任意サイズ」を pointerup で
        // 判別する。ここではドラッグ開始点と開始ツールを記録するだけにとどめ、確定は pointerup へ委ねる。
        stateRef.current.shapePlacing = true;
        stateRef.current.shapeStart = {
          stageX: stagePoint.x,
          stageY: stagePoint.y,
          clientX: event.clientX,
          clientY: event.clientY,
        };
        stateRef.current.shapeDragActive = false;
        stateRef.current.placeDragTool = tool;
        container.setPointerCapture(event.pointerId);
        return;
      }

      if (
        tool === "place-sticky" ||
        tool === "place-text" ||
        tool === "place-stamp" ||
        tool === "place-comment"
      ) {
        // place-comment はオブジェクトを即作成せず、クリック位置に入力ウィンドウを開く
        // （実際の作成は入力確定時）。onPlaceObject 側でツール種別に応じて分岐する。
        handlers.onPlaceObject({ stagePoint });
        useCanvasStore.getState().setTool("idle");
        return;
      }

      if (tool === "idle" && event.target === container) {
        // 空白部の左クリックで既存の選択を解除する。ただし追加選択の修飾キー（Ctrl/Meta/Shift）を
        // 押しているときは解除しない——反転 ON では左ドラッグが範囲選択（マーキー）で、ここで解除すると
        // use-marquee-selection が pointerup で参照する既存選択が空になり、Ctrl+ドラッグの「追加選択」が
        // 「置換」に化けてしまう（追加選択の破壊を防ぐ）。反転 OFF でも修飾キー無しの単純クリックでのみ解除する。
        const additive = event.ctrlKey || event.metaKey || event.shiftKey;
        if (!additive) {
          useCanvasStore.getState().setSelectedObjectIds([]);
        }
        // 反転 OFF では左ドラッグでパン。反転 ON では左は範囲選択（マーキー）担当のためパンしない。
        if (event.button === panButton) {
          beginPan(event, view, tool, false);
        }
      }
    };

    const onPointerMove = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      // 参照専用（埋め込み）: 保留中のパンがしきい値を超えてドラッグされたら、この時点でパンを開始する。
      // 開始点は pointerdown 時点の位置・view を採用し、以降の移動量をそのまま反映する（開始時の飛びを防ぐ）。
      const readOnlyPanPending = stateRef.current.readOnlyPanPending;
      if (readOnlyPanPending && !stateRef.current.panning) {
        // 保留を張ったポインタ以外の pointermove（多点タッチの別指など）ではパンを開始しない
        // （別ポインタで保留の開始点・pointerId を流用しないため。保留自体はこの指の pointerup / window の
        // pointerup で解除される）。
        if (event.pointerId !== readOnlyPanPending.pointerId) return;
        // ボタンが 1 つも押されていない pointermove では保留を破棄する（防御的。しきい値到達前は pointer
        // capture を取っておらず、保留中に container 外へ出て pointerup を取りこぼす経路の保険。通常は
        // window の pointerup / pointercancel で解除される）。参照専用は左/中/右いずれのボタンでもパンする
        // ため、主ボタン（ビット 0）だけでなく「全ボタンが離された（buttons===0）」で判定する。
        if (event.buttons === 0) {
          stateRef.current.readOnlyPanPending = null;
          return;
        }
        const distance = Math.hypot(
          event.clientX - readOnlyPanPending.x,
          event.clientY - readOnlyPanPending.y
        );
        if (distance <= READONLY_PAN_ACTIVATE_PX) return;
        stateRef.current.panning = true;
        stateRef.current.panStart = {
          x: readOnlyPanPending.x,
          y: readOnlyPanPending.y,
          panX: readOnlyPanPending.panX,
          panY: readOnlyPanPending.panY,
        };
        // 右ボタン起点のパンなら、ドラッグ成立後の contextmenu を抑止する（下の panning ブロックで
        // panMoved 到達時に suppressContextMenuRef を立てる）。他ボタンでは抑止しない。
        stateRef.current.panMoved = false;
        stateRef.current.panIsSecondary = readOnlyPanPending.isSecondary;
        stateRef.current.readOnlyPanPending = null;
        useCanvasStore.getState().setTool("panning");
        container.setPointerCapture(readOnlyPanPending.pointerId);
        // この後の panning ブロックで、開始点からの移動量に応じて view を平行移動する。
      }

      // 図形 / セクションのドラッグ配置中: しきい値を超えたらプレビュー枠を更新する。

      const shapeStart = stateRef.current.shapeStart;
      const placeDragTool = stateRef.current.placeDragTool;
      if (stateRef.current.shapePlacing && shapeStart && placeDragTool) {
        // Esc などで resetMode され、ツールが開始ツールから外れたらジェスチャを終了する。
        // これをしないと、取り消し後の pointermove でプレビュー枠が復活してしまう
        // （最終的な配置は pointerup 側で抑止済み）。
        if (useCanvasStore.getState().tool !== placeDragTool) {
          stateRef.current.shapePlacing = false;
          stateRef.current.shapeStart = null;
          stateRef.current.shapeDragActive = false;
          stateRef.current.placeDragTool = null;
          useCanvasStore.getState().setShapePlacementDraft(null);
          return;
        }
        const distance = Math.hypot(
          event.clientX - shapeStart.clientX,
          event.clientY - shapeStart.clientY
        );
        if (!stateRef.current.shapeDragActive) {
          // しきい値未満の手ブレでは枠を出さない（シングルクリック配置を保護する）。
          if (distance <= SHAPE_DRAG_THRESHOLD_PX) return;
          stateRef.current.shapeDragActive = true;
        }
        const frame = computeShapeDragFrame(
          shapeStart.stageX,
          shapeStart.stageY,
          stagePoint.x,
          stagePoint.y,
          placeDragFrameOptions(placeDragTool)
        );
        useCanvasStore.getState().setShapePlacementDraft({
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          arrowFromCorner: frame.arrowFromCorner,
        });
        return;
      }

      if (stateRef.current.drawing) {
        const clippedStagePoint = clampPointToCanvas(stagePoint);
        stateRef.current.points.push({
          x: clippedStagePoint.x,
          y: clippedStagePoint.y,
          pressure: pressureOf(event),
        });
        // 入力中ストロークは Canvas2D に増分描画する（暫定：全描画 redraw でなく
        // 直前ポイントから現在ポイントまでをライン描画しても十分滑らか）
        const handle = options.penLayerRef.current;
        if (handle) {
          const ctx = handle.canvas.getContext("2d");
          if (ctx) {
            const color = useCanvasStore.getState().penColor;
            const thickness = PEN_THICKNESS_PIXELS[useCanvasStore.getState().penThickness];
            ctx.save();
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.strokeStyle = color;
            ctx.lineWidth = thickness;
            const points = stateRef.current.points;
            const last = points[points.length - 2];
            const cur = points[points.length - 1];
            if (last && cur) {
              ctx.beginPath();
              // ステージ座標からプレビュー矩形（固定ステージ ∪ キャンバス矩形）の原点を差し引いて
              // プレビュー Canvas ローカル座標へ変換する。
              ctx.moveTo(last.x - penPreviewRect.x, last.y - penPreviewRect.y);
              ctx.lineTo(cur.x - penPreviewRect.x, cur.y - penPreviewRect.y);
              ctx.stroke();
            }
            ctx.restore();
          }
        }
        // 他参加者へ描画中ストロークの増分点を中継する（一定間隔で間引く）。
        const onStrokeProgress = handlers.onStrokeProgress;
        const strokeId = stateRef.current.strokeId;
        if (onStrokeProgress && strokeId) {
          const now = Date.now();
          if (now - stateRef.current.lastTransientAt >= PEN_TRANSIENT_THROTTLE_MS) {
            const allPoints = stateRef.current.points;
            const newPoints = allPoints.slice(stateRef.current.sentPointCount);
            if (newPoints.length > 0) {
              stateRef.current.lastTransientAt = now;
              stateRef.current.sentPointCount = allPoints.length;
              onStrokeProgress({ strokeId, points: newPoints });
            }
          }
        }
        return;
      }

      if (stateRef.current.panning && stateRef.current.panStart) {
        const start = stateRef.current.panStart;
        // しきい値を超えて動いたら「実際にドラッグした」とみなす。右ボタン起点のパンが動いたときは、
        // 直後の contextmenu を抑止対象にする（パン操作がメニュー表示に化けないようにする）。
        if (!stateRef.current.panMoved) {
          const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
          if (distance > PAN_MOVE_THRESHOLD_PX) {
            stateRef.current.panMoved = true;
            if (stateRef.current.panIsSecondary) {
              suppressContextMenuRef.current = true;
            }
          }
        }
        useCanvasStore.getState().setView({
          ...view,
          panX: start.panX + (event.clientX - start.x),
          panY: start.panY + (event.clientY - start.y),
        });
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      // 図形 / セクションの配置ジェスチャの確定。ドラッグ成立なら任意サイズ、未成立なら
      // シングルクリック扱い。
      if (stateRef.current.shapePlacing) {
        const shapeStart = stateRef.current.shapeStart;
        const dragActive = stateRef.current.shapeDragActive;
        const placeDragTool = stateRef.current.placeDragTool;
        // 配置せず状態だけ破棄するケース:
        // - pointercancel（ブラウザによる中断）
        // - ドラッグ中に Esc などでツールが開始ツールから外れた（resetMode による取り消し）
        const cancelled =
          event.type === "pointercancel" ||
          placeDragTool === null ||
          useCanvasStore.getState().tool !== placeDragTool;
        stateRef.current.shapePlacing = false;
        stateRef.current.shapeStart = null;
        stateRef.current.shapeDragActive = false;
        stateRef.current.placeDragTool = null;
        useCanvasStore.getState().setShapePlacementDraft(null);
        if (shapeStart && placeDragTool && !cancelled) {
          if (dragActive) {
            const view = useCanvasStore.getState().view;
            const end = screenToStage({ x: event.clientX, y: event.clientY }, view);
            const frame = computeShapeDragFrame(
              shapeStart.stageX,
              shapeStart.stageY,
              end.x,
              end.y,
              placeDragFrameOptions(placeDragTool)
            );
            if (placeDragTool === "place-section") {
              handlers.onPlaceSectionDrag({ frame });
            } else {
              handlers.onPlaceShapeDrag({ frame });
            }
          } else {
            // しきい値未満＝シングルクリック。従来どおり既定サイズで配置する。
            handlers.onPlaceObject({
              stagePoint: { x: shapeStart.stageX, y: shapeStart.stageY },
            });
          }
        }
        // ツールが開始ツールのままのとき（=外部から変更されていないとき）だけ idle へ戻す。
        // Esc やツール切替で既に別ツールへ変わっている場合は、その選択を尊重して上書きしない。
        if (placeDragTool && useCanvasStore.getState().tool === placeDragTool) {
          useCanvasStore.getState().setTool("idle");
        }
        if (container.hasPointerCapture(event.pointerId)) {
          container.releasePointerCapture(event.pointerId);
        }
        return;
      }
      if (stateRef.current.drawing) {
        const points = stateRef.current.points;
        const strokeId = stateRef.current.strokeId;
        stateRef.current.drawing = false;
        if (points.length > 1 && strokeId) {
          const color = useCanvasStore.getState().penColor;
          const thickness = PEN_THICKNESS_PIXELS[useCanvasStore.getState().penThickness];
          handlers.onStrokeCommit({ strokeId, points, color, thickness });
          // 入力中専用レイヤは、確定後にオブジェクトレイヤへ描画されるため消去する。
          const handle = options.penLayerRef.current;
          if (handle) {
            const ctx = handle.canvas.getContext("2d");
            ctx?.clearRect(0, 0, handle.canvas.width, handle.canvas.height);
          }
        }
        stateRef.current.points = [];
        // ストローク確定後は中継状態をリセットする（確定 op 側でゴーストが破棄される）。
        stateRef.current.strokeId = null;
        stateRef.current.sentPointCount = 0;
      }
      // 参照専用のパン保留（しきい値未満で離した＝その場クリック）は破棄する。
      stateRef.current.readOnlyPanPending = null;
      if (stateRef.current.panning) {
        stateRef.current.panning = false;
        stateRef.current.panStart = null;
        // パン開始時のツールへ復元する（中ボタンパンで配置ツール中にパンした場合など）。
        // 空白部の通常パンは開始時 idle なので、従来どおり idle へ戻る。
        const restore = stateRef.current.toolBeforePan ?? "idle";
        stateRef.current.toolBeforePan = null;
        stateRef.current.panMoved = false;
        stateRef.current.panIsSecondary = false;
        useCanvasStore.getState().setTool(restore);
        // pointercancel（ブラウザ/OS 中断・タッチキャンセル）で終わった右ボタンパンの後は contextmenu が
        // 発火しないため、立てた抑止フラグを残さない（次の正当な contextmenu を誤抑止しないため）。
        if (event.type === "pointercancel") {
          suppressContextMenuRef.current = false;
        }
      }
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    // 参照専用パンの保留（readOnlyPanPending）は、しきい値到達まで pointer capture を取らないため、
    // 保留中に container 外へポインタが出て pointerup を container で取りこぼすと保留が残り得る。
    // ポインタが「どこで離されても」保留を確実に解除するため、window でも pointerup / pointercancel を
    // 監視して保留を破棄する（capture を取らない＝リンクカード等のクリックは阻害しない、という前提を維持
    // したまま、取りこぼしによる保留リークを塞ぐ）。pan 開始後は capture 済みで container 側が受け取るため、
    // ここでは保留の解除だけを担い、panning 状態は container の onPointerUp が確定的に片付ける。
    const clearReadOnlyPanPending = (): void => {
      stateRef.current.readOnlyPanPending = null;
    };

    // 右ボタンドラッグによるパン（反転 ON）直後の contextmenu を抑止する。
    // window の capture フェーズで、ブラウザ標準メニュー・空白部の貼り付けメニューより先に止める。
    // 抑止対象はパンが実際に動いたときだけ（右シングルクリックのメニューは従来どおり出す）。
    const onContextMenu = (event: MouseEvent): void => {
      if (!suppressContextMenuRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressContextMenuRef.current = false;
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("pointerup", clearReadOnlyPanPending);
    window.addEventListener("pointercancel", clearReadOnlyPanPending);
    window.addEventListener("contextmenu", onContextMenu, { capture: true });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("pointerup", clearReadOnlyPanPending);
      window.removeEventListener("pointercancel", clearReadOnlyPanPending);
      window.removeEventListener("contextmenu", onContextMenu, { capture: true });
    };
  }, [
    handlers,
    options.penLayerRef,
    options.stageRef,
    readOnly,
    penPreviewRect.x,
    penPreviewRect.y,
  ]);
}

function pressureOf(event: PointerEvent): number {
  // ペンデバイスのみ筆圧を採用、それ以外は均一な太さで描画
  if (event.pointerType === "pen" && event.pressure > 0) {
    return event.pressure;
  }
  return 0.5;
}
